// Email marketing's sending side (ADR 0049, docs/specs/email-marketing.md 2.4 to 2.6, 2.15;
// story EM12 with the API half of EM11). Mirrors crates/api-types/src/sending.rs and the
// vd_core types it re-exports (`vd_core::dns`, `vd_core::marketing::limits`). Every route is
// behind `email_marketing`: the console's `/v1/admin/...` routes need an Admin (a team's design
// rule also lets that team's manager in); the From picker is the member's own.

import type { ApiClient } from "./client.ts";
import type { Plan } from "./plans.ts";
import type { Timestamp, Uuid } from "./types.ts";

// DNS records (vd_core::dns) -----------------------------------------------------------------

export type DomainStatus = "pending" | "verified" | "failing";
export type RecordType = "CNAME" | "MX" | "TXT";
export type RecordPurpose = "dkim" | "dmarc" | "tracking_host";
export type RecordStatus = "found" | "missing";

export interface ExpectedRecord {
  purpose: RecordPurpose;
  type: RecordType;
  name: string;
  value: string;
  priority?: number | null;
  /** False for an existing DMARC policy, which Kite never touches. */
  write: boolean;
  /** False for the tracking host. */
  required: boolean;
}

export interface RecordCheck {
  record: ExpectedRecord;
  status: RecordStatus;
}

// Limits (vd_core::marketing::limits) --------------------------------------------------------

export type Binding = "mailbox" | "warmup" | "plan" | "address" | "person" | "paused";

export interface Remaining {
  left: number;
  /** The mailbox's daily cap ("of 150"); none for company addresses. */
  of?: number | null;
  binds: Binding;
}

// Sending domains -----------------------------------------------------------------------------

export type DomainPauseReason =
  | "bounces"
  | "complaints"
  | "dns_changed"
  | "provider_reputation"
  | "admin";
export type HealthKind = "warming_up" | "healthy" | "needs_attention" | "paused";
export type IdentityState = "creating" | "ready" | "failed";

export interface DomainHealth {
  kind: HealthKind;
  /** "Warming up (day 6)", "Healthy", "Needs attention", "Paused (DNS records changed)". */
  label: string;
  paused_reason?: DomainPauseReason | null;
  warmup_day?: number | null;
}

export interface WarmupView {
  step: number;
  cap: number;
  warming: boolean;
}

export interface CloudflareView {
  zone_name: string;
  connected_at?: Timestamp | null;
  written_at?: Timestamp | null;
  written: number;
  skipped: string[];
  error?: string | null;
}

export interface SendingDomain {
  id: Uuid;
  domain: string;
  state: DomainStatus;
  /** "Verifying", "Ready", "Failing". */
  dns_label: string;
  records: RecordCheck[];
  identity: IdentityState;
  identity_error?: string | null;
  health: DomainHealth;
  warmup: WarmupView;
  sent_today: number;
  delivered_7d: number;
  bounces_7d: number;
  complaints_7d: number;
  tracking_host?: string | null;
  cloudflare?: CloudflareView | null;
  addresses: number;
  created_by_name?: string | null;
  created_at: Timestamp;
  verified_at?: Timestamp | null;
  last_checked_at?: Timestamp | null;
  paused_at?: Timestamp | null;
}

export interface SendingDomainList {
  items: SendingDomain[];
}

export interface DomainDay {
  day: string;
  cap?: number | null;
  sent: number;
  delivered: number;
  bounced: number;
  complained: number;
  unsubscribed: number;
}

export interface SendingDomainDetail {
  domain: SendingDomain;
  days: DomainDay[];
}

// Company addresses and grants ----------------------------------------------------------------

export type Transport = "kite" | "microsoft" | "google";
/** `self`: the company's mailbox (`reply_to_address`, or the address itself). */
export type ReplyTo = "self" | "sender";
export type AddressStatus = "ready" | "domain_pending" | "paused" | "archived";
export type GrantRole = "admin" | "manager" | "rep";

export type Grantee =
  | { kind: "user"; user_id: Uuid }
  | { kind: "team"; manager_user_id: Uuid }
  | { kind: "role"; role: GrantRole }
  | { kind: "seat"; plan: Plan };

export interface SendingGrant {
  id: Uuid;
  company_mailbox_id: Uuid;
  grantee: Grantee;
  /** "Jared Bailey", "Ana Ruiz's team", "Admins", "Marketing seats". */
  label: string;
  daily_cap?: number | null;
  created_at: Timestamp;
}

export interface SendingGrantList {
  items: SendingGrant[];
}

export interface NewSendingGrant {
  company_mailbox_id: Uuid;
  grantee: Grantee;
  daily_cap?: number | null;
}

export interface CompanyAddress {
  id: Uuid;
  address: string;
  display_name?: string | null;
  transport: Transport;
  reply_to: ReplyTo;
  reply_to_address?: string | null;
  /** Empty: "Domain only". */
  daily_cap?: number | null;
  sending_domain_id?: Uuid | null;
  status: AddressStatus;
  grants: SendingGrant[];
  sent_30d: number;
  created_at: Timestamp;
  archived_at?: Timestamp | null;
}

export interface CompanyAddressList {
  items: CompanyAddress[];
}

export interface NewCompanyAddress {
  address: string;
  display_name?: string | null;
  transport: Transport;
  reply_to: ReplyTo;
  reply_to_address?: string | null;
  daily_cap?: number | null;
}

/** Only the fields sent change; null clears an optional one. */
export interface PatchCompanyAddress {
  display_name?: string | null;
  reply_to?: ReplyTo;
  reply_to_address?: string | null;
  daily_cap?: number | null;
}

// The From picker -----------------------------------------------------------------------------

export type FromKind = "mailbox" | "company";

export interface SendingAddress {
  kind: FromKind;
  /** `mailboxes.id` or `company_mailboxes.id`: what a send's From names. */
  id: Uuid;
  address: string;
  display_name?: string | null;
  /** "Microsoft 365", "Google", "Sends through Kite", "Microsoft 365 shared mailbox". */
  sends_through: string;
  status: AddressStatus;
  remaining: Remaining;
  /** "142 of 150 left today", "312 left today", "Paused", "Domain not ready". */
  caption: string;
  /** "Warm-up", "Your limit on this address", "Address limit", "Paused". */
  binds_label?: string | null;
  /** A connected mailbox: reaches prospects and known contacts. */
  mailbox?: boolean;
  /** Bulk sending: known contacts only. */
  bulk?: boolean;
}

export interface SendingAddresses {
  mailboxes: SendingAddress[];
  company: SendingAddress[];
}

// Settings and the sending policy -------------------------------------------------------------

export interface TeamDesignRule {
  manager_user_id: Uuid;
  team: string;
  reps_can_design: boolean;
  /** False: the team follows the company setting. */
  overridden: boolean;
  set_by_name?: string | null;
  set_at?: Timestamp | null;
}

export interface MarketingSettings {
  reps_can_design: boolean;
  approval_required: boolean;
  mailbox_daily_cap: number;
  ai_images: boolean;
  weekly_cap: number;
  track_opens: boolean;
  test_send_max_recipients: number;
  test_sends_per_hour: number;
  test_sends_per_design_day: number;
  teams: TeamDesignRule[];
  updated_at?: Timestamp | null;
  updated_by_name?: string | null;
}

export type PutMarketingSettings = Partial<
  Omit<MarketingSettings, "teams" | "updated_at" | "updated_by_name">
>;

export interface MemberMailboxCap {
  user_id: Uuid;
  daily?: number | null;
  effective: number;
}

export interface PolicyAcceptance {
  version: string;
  accepted_by: Uuid;
  accepted_by_name?: string | null;
  accepted_at: Timestamp;
}

export interface SendingPolicy {
  version: string;
  /** Markdown. */
  text: string;
  text_sha256: string;
  accepted?: PolicyAcceptance | null;
}

const enc = encodeURIComponent;

export class SendingApi {
  constructor(private readonly client: ApiClient) {}

  /** The From picker and the desktop Integrations cards. */
  sendingAddresses(signal?: AbortSignal) {
    return this.client.get<SendingAddresses>("/v1/me/sending-addresses", signal);
  }

  domains(signal?: AbortSignal) {
    return this.client.get<SendingDomainList>("/v1/admin/sending-domains", signal);
  }
  domain(id: Uuid, signal?: AbortSignal) {
    return this.client.get<SendingDomainDetail>(`/v1/admin/sending-domains/${enc(id)}`, signal);
  }
  addDomain(domain: string) {
    return this.client.post<SendingDomain>("/v1/admin/sending-domains", { domain });
  }
  /** Looks the records up now (409 `identity_not_ready`, 503 `dns_unavailable`). */
  verifyDomain(id: Uuid) {
    return this.client.post<SendingDomain>(`/v1/admin/sending-domains/${enc(id)}/verify`);
  }
  pauseDomain(id: Uuid) {
    return this.client.post<SendingDomain>(`/v1/admin/sending-domains/${enc(id)}/pause`);
  }
  /** Two warm-up steps lower (409 `records_missing` while DNS is failing). */
  resumeDomain(id: Uuid) {
    return this.client.post<SendingDomain>(`/v1/admin/sending-domains/${enc(id)}/resume`);
  }
  /** 409 `addresses_on_domain` while addresses use it. */
  removeDomain(id: Uuid) {
    return this.client.del<void>(`/v1/admin/sending-domains/${enc(id)}`);
  }
  /** A token for the domain's zone with DNS edit rights; Kite writes the missing records. */
  connectCloudflare(id: Uuid, apiToken: string) {
    return this.client.put<SendingDomain>(`/v1/admin/sending-domains/${enc(id)}/cloudflare`, {
      api_token: apiToken,
    });
  }
  disconnectCloudflare(id: Uuid) {
    return this.client.del<SendingDomain>(`/v1/admin/sending-domains/${enc(id)}/cloudflare`);
  }

  addresses(signal?: AbortSignal) {
    return this.client.get<CompanyAddressList>("/v1/admin/company-addresses", signal);
  }
  /** 422 `domain_not_added`, `domain_not_verified`, `reply_to_on_sending_domain`, `reply_to_needed`. */
  addAddress(input: NewCompanyAddress) {
    return this.client.post<CompanyAddress>("/v1/admin/company-addresses", input);
  }
  updateAddress(id: Uuid, patch: PatchCompanyAddress) {
    return this.client.patch<CompanyAddress>(`/v1/admin/company-addresses/${enc(id)}`, patch);
  }
  archiveAddress(id: Uuid) {
    return this.client.del<void>(`/v1/admin/company-addresses/${enc(id)}`);
  }

  grants(address?: Uuid, signal?: AbortSignal) {
    const q = address ? `?address=${enc(address)}` : "";
    return this.client.get<SendingGrantList>(`/v1/admin/sending-grants${q}`, signal);
  }
  addGrant(input: NewSendingGrant) {
    return this.client.post<SendingGrant>("/v1/admin/sending-grants", input);
  }
  setGrantCap(id: Uuid, dailyCap: number | null) {
    return this.client.patch<SendingGrant>(`/v1/admin/sending-grants/${enc(id)}`, {
      daily_cap: dailyCap,
    });
  }
  revokeGrant(id: Uuid) {
    return this.client.del<void>(`/v1/admin/sending-grants/${enc(id)}`);
  }

  settings(signal?: AbortSignal) {
    return this.client.get<MarketingSettings>("/v1/admin/marketing-settings", signal);
  }
  putSettings(input: PutMarketingSettings) {
    return this.client.put<MarketingSettings>("/v1/admin/marketing-settings", input);
  }
  teamRule(manager: Uuid, signal?: AbortSignal) {
    return this.client.get<TeamDesignRule>(
      `/v1/admin/marketing-settings/teams/${enc(manager)}`,
      signal,
    );
  }
  putTeamRule(manager: Uuid, repsCanDesign: boolean) {
    return this.client.put<TeamDesignRule>(`/v1/admin/marketing-settings/teams/${enc(manager)}`, {
      reps_can_design: repsCanDesign,
    });
  }
  removeTeamRule(manager: Uuid) {
    return this.client.del<TeamDesignRule>(`/v1/admin/marketing-settings/teams/${enc(manager)}`);
  }
  memberMailboxCap(user: Uuid, signal?: AbortSignal) {
    return this.client.get<MemberMailboxCap>(
      `/v1/admin/marketing-settings/members/${enc(user)}`,
      signal,
    );
  }
  /** 1 to 500; null uses the company default. */
  setMemberMailboxCap(user: Uuid, daily: number | null) {
    return this.client.put<MemberMailboxCap>(`/v1/admin/marketing-settings/members/${enc(user)}`, {
      daily,
    });
  }

  policy(signal?: AbortSignal) {
    return this.client.get<SendingPolicy>("/v1/admin/sending-policy", signal);
  }
  /** Echo the version and hash that were shown (409 `policy_changed`). */
  acceptPolicy(version: string, textSha256: string) {
    return this.client.post<SendingPolicy>("/v1/admin/sending-policy/accept", {
      version,
      text_sha256: textSha256,
    });
  }
}
